import { ReactElement, useCallback, useRef } from 'react';

import { useHitSwatches } from '@hooks/useHitSwatches';
import { useLocale } from '@hooks/useLocale';
import type { HitProps } from '@components/algolia/Hit';
import MiniProductCard from '_components/UI/Cards/MiniProductCard';
import QuickAdd from '_components/QuickAdd';
import { FEATURE_FLAG_QUICK_ADD } from '@config/feature-flag';
import { isGiftCardHit } from '@utils/algolia/isGiftCardHit';
import { colourKeyForVariantSku } from '@utils/algolia/colourKeyForVariantSku';
import {
  HOTSPOT_ITEM_LIST_NAME,
  trackHotspotQuickAddClick,
} from '_utils/GA4/trackHotspotEvents';

import styles from './ProductTile.module.scss';

export interface ProductTileProps {
  product: HitProps;
  /** Optional colourway SKU (`ImageHotspotDot.variantSku`) pinning this
      hotspot to a specific variant rather than the product's default
      colourway — resolved to a colour key via `colourKeyForVariantSku`,
      the same helper `PlpCrossLinkingCard` uses for its equivalent
      "Prismic-curated SKU, live pricing" case. */
  variantSku?: string | null;
  /** Forwarded to `QuickAdd`'s own `onOpenChange` — `HotspotPopover` uses
      this to keep its outer popover open while this one's is, since
      `QuickAdd`'s portaled popup content sits outside `.tile`'s DOM subtree.
      See `QuickAddProps.onOpenChange`'s own doc for why. */
  onQuickAddOpenChange?: (open: boolean) => void;
  /** Forwarded to `QuickAdd`'s own `onPendingChange` — `ImageHotspot`
      (via `HotspotPopover`) uses this to keep this hotspot's tile pinned
      open while its add is in flight, even if another hotspot dot (or a
      tab) is clicked. See `QuickAddProps.onPendingChange`'s own doc. */
  onQuickAddPendingChange?: (pending: boolean) => void;
  /** ED-447: context for `click_hotspot_quick_atc` and the standard
      `add_to_cart` event's `item_list_name`/`coop_brand` — this component
      has no other way to know its own page/tab/position. */
  interactionLocation: string;
  tabName: string;
  coOpBrand: string | null;
  /** 0-based position of this hotspot within its tab. */
  index: number;
}

/**
 * The product tile a hotspot opens: `MiniProductCard` (Figma "Mini Product
 * Card", node 419:6774 — see its own doc) plus a quick-add-to-cart trigger
 * as its `cartAction` slot — composes the same `QuickAdd` component the PLP
 * grid card uses (popover, size grid, success state), rather than a bespoke
 * cart flow, with `compact` forcing its always-visible primary icon trigger
 * face at every breakpoint (this card is too narrow for the PLP grid's
 * hover-CTA text button to read well). The popover body still splits by
 * viewport: a narrow one gets the same bottom sheet as the PLP grid card,
 * but a wider one gets its own stacked card (Figma 801:83103) anchored
 * below `tileRef` — the whole row, not just the trigger's own slot — rather
 * than floating over an image the way the PLP grid card's real desktop
 * panel does, since this card has no image to overlay. `tileRef` sits on
 * this component's own outer wrapper (sized/positioned for the hotspot
 * popover, via `.tile`) rather than on `MiniProductCard` itself, which has
 * no positioning concerns of its own — see its doc.
 * Prices through `useHitSwatches`, on `variantSku`'s colourway when authored
 * (falling back to the product's own default colourway otherwise) — the same
 * hook and `colourKeyForVariantSku` resolution `PlpCrossLinkingCard` uses for
 * an equivalent "Prismic-curated SKU, live pricing" case, so member/customer-
 * group pricing matches the rest of the site instead of being recomputed here.
 *
 * Positioning (near the hotspot dot, flipped/shifted to stay on-image) is
 * owned entirely by `HotspotPopover`'s Base UI `Popover.Positioner` — this
 * component is just the card's content, with no `x`/`y`/`align` of its own.
 */
const ProductTile = ({
  product,
  variantSku,
  onQuickAddOpenChange,
  onQuickAddPendingChange,
  interactionLocation,
  tabName,
  coOpBrand,
  index,
}: ProductTileProps): ReactElement => {
  const locale = useLocale();
  // Anchors the stacked size-selector/success card (Figma 801:83103) below
  // the whole row, not just QuickAdd's own 56px trigger slot — see the
  // component doc and `QuickAddProps.stackedAnchorRef`.
  const tileRef = useRef<HTMLDivElement>(null);
  const initialColour = colourKeyForVariantSku(product, variantSku);
  const { activeVariant, swatches } = useHitSwatches(
    initialColour,
    product.variants,
    {
      image: product.image,
      imageTransparent: product.imageTransparent,
      badge: product.badge ?? null,
      badgeGuest: product.badgeGuest ?? null,
      price: product.price,
      salePrice: product.salePrice,
      sku: product.sku,
    },
    locale
  );

  const image =
    activeVariant?.imageTransparent ||
    activeVariant?.image ||
    product.imageTransparent ||
    product.image ||
    '/images/pdp/product-placeholder.jpg';
  // Range before single formatted price — same fallback order as the PLP
  // grid's own price props (`_components/Products/Hit/index.tsx`) — a
  // colour with several sizes at different price points has no single
  // `priceFormatted` to fall back to (ED-345 QA #8).
  const displayPrice =
    activeVariant?.priceRange?.price || activeVariant?.priceFormatted || '';
  const salePrice =
    activeVariant?.priceRange?.salePrice ||
    activeVariant?.salePriceFormatted ||
    undefined;

  // Same guard as the PLP grid's two Quick Add callers
  // (`_components/Products/Hit/index.tsx`, `components/algolia/Hit/index.tsx`)
  // — without it, disabling the Quick Add rollout still left the trigger
  // active in hotspots, and gift cards got one too.
  const activeColourKey = activeVariant?.swatchName ?? product.colour;
  const activeColourVariant = product.variants?.[activeColourKey];
  const activeColourAvailableOnlinePurchase =
    activeColourVariant?.availableOnlinePurchase;
  const showQuickAdd =
    FEATURE_FLAG_QUICK_ADD &&
    product.availableOnPlp !== false &&
    activeColourAvailableOnlinePurchase !== false &&
    !isGiftCardHit(product.parentSku);

  // Stable identity so `QuickAdd`'s own `handleOpenChange` (which lists
  // `onTriggerOpen` as a dependency) doesn't get a new function every render.
  const handleQuickAddTriggerOpen = useCallback(() => {
    trackHotspotQuickAddClick({
      data: {
        interactionLocation,
        linkUrl: product.url,
        linkText: product.title,
        tabName,
        coOpBrand,
        index,
      },
    });
  }, [
    interactionLocation,
    product.url,
    product.title,
    tabName,
    coOpBrand,
    index,
  ]);

  // ED-345 QA (#7): this tile shows one static colourway until the shopper
  // picks another one inside QuickAdd's own colour picker — that popover has
  // no image/price display of its own, so its `onColourChange` is used here
  // to update the same `useHitSwatches` state a direct swatch click would,
  // keeping this tile's image/price in sync with what's being configured.
  const handleQuickAddColourChange = useCallback(
    (colourName: string) => {
      const matchedSwatch = swatches.find(
        (swatch) => swatch.swatchName.toLowerCase() === colourName.toLowerCase()
      );
      matchedSwatch?.onClick();
    },
    [swatches]
  );

  return (
    <div ref={tileRef} className={styles.tile}>
      <MiniProductCard
        productUrl={product.url}
        productName={product.title}
        imageSrc={image}
        displayPrice={displayPrice}
        salePrice={salePrice}
        cartAction={
          showQuickAdd && (
            <QuickAdd
              compact
              stackedAnchorRef={tileRef}
              productCode={product.parentSku}
              productTitle={product.title}
              productUrl={product.url}
              initialColour={{
                colourName: activeColourKey,
                colourSku: activeVariant?.sku ?? product.sku,
              }}
              onOpenChange={onQuickAddOpenChange}
              onPendingChange={onQuickAddPendingChange}
              onColourChange={handleQuickAddColourChange}
              analytics={{
                source: 'hotspot',
                itemListName: HOTSPOT_ITEM_LIST_NAME,
                coOpBrand,
              }}
              onTriggerOpen={handleQuickAddTriggerOpen}
            />
          )
        }
      />
    </div>
  );
};

export default ProductTile;
